// src/components/guards/ModeGate.tsx
import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

type Mode = "creator" | "brand";
const MODE_KEY = "appcreativa_mode";

function isMode(v: unknown): v is Mode {
  return v === "creator" || v === "brand";
}

function getStoredMode(): Mode | null {
  try {
    const v = localStorage.getItem(MODE_KEY);
    return isMode(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * ✅ ModeGate (única fuente de verdad) - FIX Preview
 *
 * Reglas:
 * 1) Si NO hay sesión (Preview): NO forzamos modo. Dejamos entrar a /dashboard y /brand
 *    y el bloqueo lo hacen AppShell/Dashboard/BrandCreate por acción.
 *
 * 2) Si HAY sesión:
 *   - si no hay modo guardado -> /mode?choose=1 (solo rutas privadas)
 *   - evitar cruces: creator no entra a brand, brand no entra a dashboard
 *   - /mode siempre permitido
 */
export default function ModeGate({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const lastNavRef = useRef<string | null>(null);

  // ✅ reset anti-loop en cada navegación real
  useEffect(() => {
    lastNavRef.current = null;
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (loading) return;

    const path = location.pathname;
    const search = location.search || "";

    // ✅ /mode SIEMPRE permitido
    if (path === "/mode") return;

    // ✅ Rutas públicas/auth que NO deben forzar modo
    const isPublic =
      path === "/" ||
      path === "/about" ||
      path === "/support" ||
      path === "/privacy" ||
      path === "/terms";

    const isAuth =
      path === "/login" ||
      path === "/register" ||
      path === "/forgot-password" ||
      path === "/update-password" ||
      path === "/signup"; // por si usas /signup

    const isMaintenance = path === "/maintenance";
    const isUpgrade = path === "/upgrade";

    if (isPublic || isAuth || isMaintenance || isUpgrade) return;

    // ✅ FIX CLAVE: si NO hay user (Preview) -> NO forzamos modo
    // Deja que AppShell/Dashboard/BrandCreate bloqueen por acción.
    if (!user) return;

    const mode = getStoredMode();

    // ✅ Si hay sesión pero NO hay modo -> selector
    if (!mode) {
      const to = "/mode?choose=1";
      const current = path + search;
      if (current === to) return;
      if (lastNavRef.current === to) return;
      lastNavRef.current = to;
      navigate(to, { replace: true });
      return;
    }

    // ✅ Anti-cruce entre áreas
    const isBrandRoute = path === "/brand" || path.startsWith("/brand/");
    const isCreatorRoute = path === "/dashboard" || path.startsWith("/dashboard/");

    if (mode === "creator" && isBrandRoute) {
      const to = "/dashboard";
      if (lastNavRef.current === to) return;
      lastNavRef.current = to;
      navigate(to, { replace: true });
      return;
    }

    if (mode === "brand" && isCreatorRoute) {
      const to = "/brand";
      if (lastNavRef.current === to) return;
      lastNavRef.current = to;
      navigate(to, { replace: true });
      return;
    }
  }, [location.pathname, location.search, navigate, user, loading]);

  return <>{children}</>;
}

